import { sb } from '../supabase';
import { runWithOptionalColumns } from './supabaseColumnFallback';
import { buildManagedBriefRow, generateManagedBriefWithAI } from './managedSourcing';
import { isPlainQuantity } from './requestQuantity';

/**
 * Create a MANAGED sourcing request from a wizard/form state object.
 *
 * Every wizard-originated request is managed — sourcing_mode='managed',
 * request_kind='managed' (decision #1). `direct` is never emitted here; it stays
 * reserved for the targeted "Post Request" + checkout flows.
 *
 * Mirrors the managed path of Requests.jsx submitNewRequest so the wizard and the
 * legacy form share one behavior: translate → insert → AI brief → admin_review.
 * quantity is stored as the plain number the form validated (isPlainQuantity —
 * was parseInt'd per decision #5 until 2026-09-30); both title_ar + title_en are
 * kept as source material for the trilingual brief.
 *
 * @returns {Promise<{ request: object|null, error: any }>}
 */
export async function createManagedRequest({ user, form, lang = 'ar' }) {
  const titleAr = String(form.title_ar || '').trim();
  const titleEn = String(form.title_en || '').trim();
  const fallbackTitle = titleAr || titleEn;
  const description = String(form.description || '').trim();
  // Callers validate with isPlainQuantity before calling (owner, 2026-09-30).
  // Stored as the trimmed plain number, not parseInt: parseInt turned a valid
  // "2.5" into 2 and "100 to 150" into 100 without a word to anyone. A value
  // that is somehow not plain is stored as null rather than guessed at.
  const qtyText = String(form.quantity ?? '').trim();

  // Insert IMMEDIATELY with the source text — translation and the AI brief are
  // enrichment, not conditions for a valid row. Every language column is
  // source-filled here, so two slow AI round-trips no longer sit between the
  // trader's tap and the success screen. Both enrichments run in the background.
  const payload = {
    buyer_id: user.id,
    title_ar: titleAr || fallbackTitle,
    title_en: titleEn || fallbackTitle,
    title_zh: titleEn || titleAr || fallbackTitle,
    quantity: isPlainQuantity(qtyText) ? qtyText : null,
    unit: form.unit || null,
    description,
    description_ar: description || null,
    description_en: description || null,
    description_zh: description || null,
    category: form.category || 'other',
    status: 'open',
    // ADDED 2026-09-21 (migration 20260921122508). The destination and the
    // trader's delivery ask, which no request form collected until today —
    // ship_city/ship_country existed with no writer at all, so get_public_rfq
    // printed the literal 'Saudi Arabia' to every factory.
    //
    // Passed through from the caller rather than defaulted here: this function
    // serves BOTH the managed wizard (KSA cities) and the factory-quote modal
    // (which serves the GCC and has its own country picker). Defaulting the
    // country here would quietly overwrite a Kuwaiti trader's answer.
    ship_city: String(form.ship_city || '').trim() || null,
    ship_country: String(form.ship_country || '').trim() || null,
    delivery_preference: form.delivery_preference || null,
    budget_per_unit: form.budget_per_unit ? parseFloat(form.budget_per_unit) : null,
    budget_currency: form.budget_per_unit ? 'SAR' : null,   // traders budget in SAR only (owner, 2026-09-29)
    payment_plan: form.payment_plan ? parseInt(form.payment_plan, 10) : null,
    sample_requirement: form.sample_requirement || null,
    // A managed request (request_kind 'managed') must carry the trader's
    // WhatsApp-VERIFIED number (owner, 2026-10-01): the server refuses it
    // otherwise, with hint phone_required / phone_not_verified, and that error
    // is returned untouched so the caller can send the trader back to the
    // phone step. RequestWizard passes VerifiedPhoneField's number here.
    // request_kind 'factory' (createFactoryRequest) is not covered.
    contact_phone: String(form.phone || '').trim() || null,
    // RFQ phase 2. Object paths in the PRIVATE request-attachments bucket, which
    // is what /f/<slug> mints signed URLs for. This replaced a public
    // product-images URL in reference_image, which neither caller ever set:
    // RequestWizard's EMPTY_FORM and createFactoryRequest's form literal both
    // lack the key, so the expression was dead before it was removed.
    //
    // Always an array, never null: the column is `text[] not null default '{}'`,
    // and get_factory_invite's union coalesces each side anyway.
    attachment_paths: Array.isArray(form.attachment_paths) ? form.attachment_paths : [],
    // True when at least one file did not upload and the request was published
    // regardless (decision §4.1: a failed upload warns, it never blocks). The
    // trader has already been told; this is how ops find the request later.
    attachments_missing: form.attachments_missing === true,
    sourcing_mode: 'managed',
    request_kind: form.request_kind || 'managed',
    managed_status: 'submitted',
    managed_review_state: 'pending',
    response_deadline: form.response_deadline || null,
  };

  // request_kind exists in prod; keep it optional so it's dropped gracefully in
  // any environment that hasn't got the column yet (same as description_*).
  const { data: request, error } = await runWithOptionalColumns({
    table: 'requests',
    payload,
    // attachment_paths / attachments_missing ride the same fallback. Both exist
    // in production (20260810000003 and 20260916094415), but a request must not
    // fail to be created because an environment is a migration behind — losing
    // the trader's whole specification over a column is the worse outcome, and
    // strippedColumns still reports it to the console.
    optionalKeys: ['description_ar', 'description_en', 'description_zh', 'request_kind', 'unit', 'contact_phone', 'attachment_paths', 'attachments_missing', 'ship_city', 'ship_country', 'delivery_preference'],
    execute: (nextPayload) => sb.from('requests').insert(nextPayload).select('*').single(),
  });

  if (error || !request?.id) {
    return { request: null, error: error || new Error('request insert failed') };
  }

  // ═══ (1) REMOVED 2026-09-21 — the client-side translation block ════════
  // The THIRD copy of the same unawaited translate-then-patch (the others
  // were in Requests.jsx and mobile's NewRequestScreen). It ran in the
  // browser tab: close the tab before it resolved and the raw source text
  // stayed in all six language columns forever, which is how a Chinese
  // factory was shown Arabic. Eight of fourteen requests were in that state.
  //
  // The SERVER owns this now — the translate-request edge function detects
  // the SCRIPT (not the field), writes all six columns, and records the
  // outcome in requests.translation_status (20260921112056).
  // ⚠️ Do not reintroduce a client translation call here.
  // ═══════════════════════════════════════════════════════════════════════

  // (2) Background AI brief → advance to admin_review. maabar-ai self-persists
  // server-side when given requestId (so a closed tab can't lose the brief); we
  // also upsert here as a fallback for environments where that edge upgrade isn't
  // deployed yet. Both writes are idempotent (upsert on request_id).
  (async () => {
    try {
      const brief = await generateManagedBriefWithAI({ request: payload, lang, requestId: request.id, buyerId: user.id });
      await sb.from('managed_request_briefs').upsert(
        buildManagedBriefRow({ requestId: request.id, buyerId: user.id, brief }),
        { onConflict: 'request_id' },
      );
      await sb.from('requests').update({
        managed_status: 'admin_review',
        managed_priority: brief.priority || 'normal',
        managed_ai_ready_at: new Date().toISOString(),
      }).eq('id', request.id);
    } catch (briefErr) {
      console.error('[createManagedRequest] background brief setup error:', briefErr?.message || briefErr);
    }
  })();

  return { request, error: null };
}
